import './CatalogItemPage.css';
import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { apiFetch } from "../../utils/api";
import { useTranslation } from "react-i18next";
import Spinner from '../../components/Spinner/Spinner';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import BackLink from '../../components/BackLink/BackLink';
import Button from '../../components/Button/Button';
import { useCartContext } from '../../hooks/useCartContext';
import { useHyphenate } from '../../hooks/useHyphenate';

const BULLET_DISPLAY_CLASS = {
  list:    null,
  compact: 'catalog-bullet-section--grid',
};

const getBulletSectionClass = (display) =>
  ['catalog-bullet-section', BULLET_DISPLAY_CLASS[display]].filter(Boolean).join(' ');

const Section = ({ section }) => {
  const isWide = section.bullets.length > 6;
  const hyphenate = useHyphenate();
  return (
    <div className={`catalog-section${isWide ? ' wide' : ''}`}>
      <div className="catalog-section-title">{section.name}</div>
      {section.type === 'keyvalue' ? (
        <ul className="catalog-key-value-section">
          {section.bullets.map((bullet, idx) => (
            <li key={idx}>
              <span className="catalog-section-bullet-key">{hyphenate(bullet.key)}</span>
              <span className="catalog-section-bullet-value">{hyphenate(bullet.value)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <ul className={getBulletSectionClass(section.display)}>
          {section.bullets.map((bullet, idx) => (
            <li key={idx} className="catalog-section-bullet-value">{hyphenate(bullet)}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

const CatalogItemPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { i18n, t } = useTranslation();
  const [item, setItem] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const { items: cartItems, addItem, removeItem } = useCartContext();
  const hyphenate = useHyphenate();

  useEffect(() => {
    const fetchItem = async () => {
      setLoading(true);
      const res = await apiFetch(`/api/public/catalog/item/${id}`, {
        headers: { Locale: i18n.language },
      });
      const data = await res.json();
      setItem(data);
      setLoading(false);

      const fromId = searchParams.get('from');
      const categoryId = fromId || data.categories?.[0];
      if (categoryId) {
        try {
          const catRes = await apiFetch(`/api/public/categories/${categoryId}`, {
            headers: { Locale: i18n.language },
          });
          const catData = await catRes.json();
          setCategory({ ...catData, _id: categoryId });
        } catch {
          // degrade gracefully — breadcrumb and label simply omit category
        }
      }
    };

    fetchItem();
  }, [id, i18n.language]);

  if (loading) return <Spinner />;
  if (!item) return <div>Item not found.</div>;

  const inCart = cartItems.some((i) => i.id === item._id);

  const handleCartToggle = () => {
    if (inCart) {
      removeItem(item._id);
    } else {
      addItem({
        id: item._id,
        name: item.name,
        image: item.image,
        categoryId: category?._id ?? item.categories?.[0],
        categoryName: category?.title ?? '',
      });
    }
  };

  return (
    <div className="catalog-item-page">
      <Breadcrumb items={[
        { label: t("catalog.breadcrumb") },
        ...(category ? [{ label: category.title, to: `/catalog/${category._id}` }] : []),
        { label: item.name },
      ]} />

      {category && (
        <BackLink to={`/catalog/${category._id}`} label={t("catalog.backTo", { category: category.title })} />
      )}

      <div className="catalog-item-hero">
        {item.image && (
          <div className="catalog-item-image-stage">
            <img src={item.image} alt={item.name} className="catalog-item-image" />
          </div>
        )}
        <div className="catalog-item-sticky">
          {category && (
            <div className="catalog-item-category-label">{category.title}</div>
          )}
          <div className="catalog-item-title">{hyphenate(item.name)}</div>
          <hr className="catalog-item-divider" />
          <div className="catalog-item-description">{hyphenate(item.description)}</div>
          <Button
            variant={inCart ? 'secondary' : 'primary'}
            className="catalog-item-cart-btn"
            onClick={handleCartToggle}
          >
            <span className="material-symbols-outlined">
              {inCart ? 'remove_shopping_cart' : 'add_shopping_cart'}
            </span>
            {inCart ? t('cart.removeFromCart') : t('cart.addToCart')}
          </Button>
        </div>
      </div>

      <div className="catalog-item-sections">
        {item.sections && item.sections.map((section, idx) => (
          <Section section={section} key={idx} />
        ))}
      </div>
    </div>
  );
};

export default CatalogItemPage;
